import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import {
  userAppAccessService,
  type UserAppAccessSummaryDto,
  type ToggleFavoriteResponse,
} from '@/app/services/api/userAppAccessService';
import type { RootState } from '@/app/store';

/**
 * Which surface owns the current userAppAccess state. Set by each home page's
 * mount effect. Thunks read this to decide whether to use the active tenant
 * (`workspace` scope) or the null-tenant bucket (`main` scope).
 * See `.kiro/specs/archive/2026-04-19-main-mobile-home/` (Sprint 2.4).
 */
export type UserAppAccessScope = 'workspace' | 'main' | null;

// Resolve the tenantId to send with userAppAccess requests. On `/main`, ALWAYS
// use null (the account-level bucket) regardless of activeTenant. On `/workspace`
// (or when scope is unregistered), use the active tenant.
function selectScopedTenantId(state: RootState): number | null {
  if (state.userAppAccess.scope === 'main') {
    return null;
  }
  const raw = state.tenant.activeTenant?.tenantId;
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export interface UserAppAccessState {
  recents: UserAppAccessSummaryDto[];
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: number | null;
  favorites: UserAppAccessSummaryDto[];
  favoritesIsLoading: boolean;
  favoritesError: string | null;
  favoritesLastFetchedAt: number | null;
  scope: UserAppAccessScope;
}

const initialState: UserAppAccessState = {
  recents: [],
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  favorites: [],
  favoritesIsLoading: false,
  favoritesError: null,
  favoritesLastFetchedAt: null,
  scope: null,
};

const RECENTS_TOP_N = 6;

export const fetchRecents = createAsyncThunk<
  UserAppAccessSummaryDto[],
  number | undefined,
  { rejectValue: string; state: RootState }
>(
  'userAppAccess/fetchRecents',
  async (take, { rejectWithValue, getState }) => {
    try {
      const tenantId = selectScopedTenantId(getState());
      return await userAppAccessService.getRecents(tenantId, take ?? RECENTS_TOP_N);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch recents'
      );
    }
  }
);

/**
 * Argument shape for `markAppAccessed`. Callers can either pass the bare
 * sysAppId (legacy) or `{sysAppId, tenantIdOverride}` to bypass the
 * slice's scope-based selector. The override is the right choice when the
 * caller has authoritative tenant context (e.g. AppShell's `tenant` prop
 * — see useTrackAppAccess docs).
 */
export type MarkAppAccessedArg =
  | number
  | { sysAppId: number; tenantIdOverride?: number | null };

export const markAppAccessed = createAsyncThunk<
  { sysAppId: number },
  MarkAppAccessedArg,
  { rejectValue: string; state: RootState }
>(
  'userAppAccess/markAppAccessed',
  async (arg, { rejectWithValue, dispatch, getState }) => {
    const sysAppId = typeof arg === 'number' ? arg : arg.sysAppId;
    const tenantIdOverride =
      typeof arg === 'number' ? undefined : arg.tenantIdOverride;
    try {
      // Anonymous users can browse public tools (weather-forecast, barcode-generator).
      // The mark-accessed endpoint is auth-required — calling it would 401,
      // which apiClient/authErrorHandler converts into a hard redirect to /login,
      // overriding the in-flight SPA navigation. Short-circuit when there's no
      // authenticated session. Recents/favorites are user-scoped anyway, so
      // there is nothing meaningful to record for guests.
      const state = getState();
      if (!state.auth.isAuthenticated) {
        return { sysAppId };
      }

      // Override wins when supplied (avoids the stale-scope null-tenant bug
      // when the user navigates straight into a workspace inner app page
      // without visiting the home view that sets scope='workspace').
      const tenantId =
        tenantIdOverride !== undefined
          ? tenantIdOverride
          : selectScopedTenantId(state);
      await userAppAccessService.markAccessed(sysAppId, tenantId);

      // Top-N bump-up: if the accessed app was NOT in the current top-N,
      // refetch so it enters the list. If it was already there, the slice's
      // `.fulfilled` reducer below reorders it client-side (move-to-front) —
      // no refetch needed and the UI reorders on the same tick. Read fresh
      // state after the await — other dispatches may have mutated recents.
      const stateAfterMark = getState();
      const inTopN = stateAfterMark.userAppAccess.recents
        .slice(0, RECENTS_TOP_N)
        .some((r) => r.id === sysAppId);
      if (!inTopN) {
        dispatch(fetchRecents(undefined));
      }

      return { sysAppId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to mark app accessed'
      );
    }
  }
);

export const fetchFavorites = createAsyncThunk<
  UserAppAccessSummaryDto[],
  void,
  { rejectValue: string; state: RootState }
>(
  'userAppAccess/fetchFavorites',
  async (_arg, { rejectWithValue, getState }) => {
    try {
      const tenantId = selectScopedTenantId(getState());
      return await userAppAccessService.getFavorites(tenantId);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch favorites'
      );
    }
  }
);

export const toggleFavorite = createAsyncThunk<
  ToggleFavoriteResponse,
  number,
  { rejectValue: string; state: RootState }
>(
  'userAppAccess/toggleFavorite',
  async (sysAppId, { rejectWithValue, getState }) => {
    try {
      const tenantId = selectScopedTenantId(getState());
      return await userAppAccessService.toggleFavorite(sysAppId, tenantId);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to toggle favorite'
      );
    }
  }
);

const userAppAccessSlice = createSlice({
  name: 'userAppAccess',
  initialState,
  reducers: {
    clearUserAppAccess: () => initialState,
    clearUserAppAccessError: (state) => {
      state.error = null;
      state.favoritesError = null;
    },
    setUserAppAccessScope: (state, action: PayloadAction<UserAppAccessScope>) => {
      state.scope = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recents = action.payload;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchRecents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'Failed to fetch recents';
      });

    // markAppAccessed is fire-and-forget — we don't surface its loading state
    // to the UI. Failures are swallowed (logged in the service layer) so
    // navigation never blocks on tracking (Sprint 2.2 Requirement 4.4).
    builder.addCase(markAppAccessed.fulfilled, (state, action) => {
      // Client-side reorder: move the accessed app to index 0 of state.recents
      // and refresh its lastAccessedAt. Mirrors the server-side sort
      // (last_accessed_at DESC) so the drawer/landing strip reflects clicks
      // immediately without waiting for a refetch.
      const { sysAppId } = action.payload;
      const idx = state.recents.findIndex((r) => r.id === sysAppId);
      if (idx < 0) return; // not in list — thunk already dispatched a refetch
      const nowIso = new Date().toISOString();
      if (idx === 0) {
        state.recents[0] = { ...state.recents[0], lastAccessedAt: nowIso };
        return;
      }
      const [entry] = state.recents.splice(idx, 1);
      state.recents.unshift({ ...entry, lastAccessedAt: nowIso });
    });
    builder.addCase(markAppAccessed.rejected, (state, action) => {
      state.error = (action.payload as string) ?? null;
    });

    builder
      .addCase(fetchFavorites.pending, (state) => {
        state.favoritesIsLoading = true;
        state.favoritesError = null;
      })
      .addCase(fetchFavorites.fulfilled, (state, action) => {
        state.favoritesIsLoading = false;
        state.favorites = action.payload;
        state.favoritesLastFetchedAt = Date.now();
      })
      .addCase(fetchFavorites.rejected, (state, action) => {
        state.favoritesIsLoading = false;
        state.favoritesError = (action.payload as string) ?? 'Failed to fetch favorites';
      });

    builder
      .addCase(toggleFavorite.fulfilled, (state, action) => {
        const { sysAppId, isFavorite, favoritedAt } = action.payload;

        // Patch recents so the star state stays in sync without a refetch.
        const recentIdx = state.recents.findIndex((r) => r.id === sysAppId);
        if (recentIdx !== -1) {
          state.recents[recentIdx] = {
            ...state.recents[recentIdx],
            isFavorite,
            favoritedAt,
          };
        }

        if (isFavorite) {
          // Adding to favorites. If the row is already in favorites (idempotent
          // same-state toggle), leave it alone; otherwise prepend.
          const existing = state.favorites.find((f) => f.id === sysAppId);
          if (!existing) {
            // Source the full SysApp payload from recents if available; otherwise
            // we leave insertion to the next fetchFavorites (fallback). Recents is
            // typically fresher than the workspace-apps slice and has the same shape.
            const source = state.recents.find((r) => r.id === sysAppId);
            if (source) {
              state.favorites = [
                { ...source, isFavorite: true, favoritedAt },
                ...state.favorites.filter((f) => f.id !== sysAppId),
              ];
            }
            // If we couldn't source the app locally, rely on the next
            // fetchFavorites refresh. Minor delay but doesn't break UX.
          }
        } else {
          state.favorites = state.favorites.filter((f) => f.id !== sysAppId);
        }
      })
      .addCase(toggleFavorite.rejected, (state, action) => {
        state.favoritesError = (action.payload as string) ?? 'Failed to toggle favorite';
      });
  },
});

export const { clearUserAppAccess, clearUserAppAccessError, setUserAppAccessScope } =
  userAppAccessSlice.actions;
export default userAppAccessSlice.reducer;

// Selectors — recents
export const selectRecents = (state: { userAppAccess: UserAppAccessState }) =>
  state.userAppAccess.recents;

export const selectRecentsIsLoading = (state: { userAppAccess: UserAppAccessState }) =>
  state.userAppAccess.isLoading;

export const selectRecentsError = (state: { userAppAccess: UserAppAccessState }) =>
  state.userAppAccess.error;

export const selectRecentsLastFetchedAt = (state: { userAppAccess: UserAppAccessState }) =>
  state.userAppAccess.lastFetchedAt;

// Selectors — favorites
export const selectFavorites = (state: { userAppAccess: UserAppAccessState }) =>
  state.userAppAccess.favorites;

export const selectFavoritesIsLoading = (state: { userAppAccess: UserAppAccessState }) =>
  state.userAppAccess.favoritesIsLoading;

export const selectFavoritesError = (state: { userAppAccess: UserAppAccessState }) =>
  state.userAppAccess.favoritesError;

export const selectFavoritesLastFetchedAt = (state: { userAppAccess: UserAppAccessState }) =>
  state.userAppAccess.favoritesLastFetchedAt;

export const selectIsFavorited = (
  state: { userAppAccess: UserAppAccessState },
  sysAppId: number
): boolean => state.userAppAccess.favorites.some((f) => f.id === sysAppId);

export const selectUserAppAccessScope = (state: { userAppAccess: UserAppAccessState }) =>
  state.userAppAccess.scope;
